import React, { useCallback, useMemo, useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './FormReactQuill.css';
import { useIntl } from 'react-intl';
import useRendersCount from '@hedtech/userenderscount';
import { makeStyles } from '@ellucian/react-design-system/core';
import {
    borderRadiusMedium,
    borderWidthThin,
    colorGlobalBorderError,
    colorTextError,
    fontSizeLarge,
    sizingXSmall,
    zIndexDropdown,
} from '@ellucian/react-design-system/core/styles/tokens';
import { useSnackbar } from '../../SnackbarContext/SnackbarContext';
import { v4 as uuid } from 'uuid';
import getPlaceholderModule from 'quill-placeholder-module';
import 'quill-placeholder-module/dist/toolbar.css';
import { richTextEditorPlaceholders } from '../../Helpers/Configuration';
import { Typography } from '@ellucian/react-design-system/core';
import { GlobalStyles } from 'tss-react';
const fonts = ['Noto', 'Roboto', 'Courier'];
const getFontName = (font) => {
    return font.toLowerCase().replace(/\s/g, '-');
};
const fontNames = fonts.map(font => getFontName(font));
const Font = ReactQuill.Quill.import('formats/font');
Font.whitelist = fontNames;
ReactQuill.Quill.register(Font, true);
ReactQuill.Quill.register('modules/placeholder', getPlaceholderModule(ReactQuill.Quill, {
    className: 'ql-placeholder-content',
}));
ReactQuill.Quill.register('modules/maxlength', (quill, options) => {
    quill.on('text-change', () => {
        let size = quill.getText();
        if (size.length > options.value)
            quill.history.undo();
    });
});

const useStyles = makeStyles({
    name: 'RichTextField'
})(() => ({
    root: {
        '& > .ql-container': {
            fontSize: fontSizeLarge,
        },
    },
    error: {
        border: `${colorGlobalBorderError} solid ${borderWidthThin}`,
        borderRadius: borderRadiusMedium,
    },
    errorText: {
        color: colorTextError,
    },
    helperText: {
        marginTop: sizingXSmall,
    },
}));
const getDefaultPlaceholders = () => richTextEditorPlaceholders;
const defaultToolbarOptions = {
    all: false,
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    blockquote: false,
    codeBlock: false,
    orderedList: false,
    bulletList: false,
    direction: false,
    header: false,
    color: false,
    backgroundColor: false,
    font: false,
    link: false,
    align: false,
    clean: false,
    formLink: false,
};
const CustomToolbar = props => {
    const {
        id,
        options: optionsProp,
        placeholders: placeholdersProp = false,
    } = props;
    const { formatMessage } = useIntl();
    const options = useMemo(() => {
        return Object.assign({}, defaultToolbarOptions, optionsProp || {});
    }, [optionsProp]);
    const placeholders = useMemo(() => {
        if (placeholdersProp === true) {
            return getDefaultPlaceholders().map(it => ({
                id: it.id,
                label: formatMessage({id: it.label})
            }));
        } else if (placeholdersProp === false) {
            return [];
        }
        return placeholdersProp;
    }, [placeholdersProp]);
    return (
        <div id={id}>
            {(options.all || options.bold) &&
                <button className="ql-bold" aria-label={formatMessage({ id: 'richTextFieldBold' })}></button>
            }
            {(options.all || options.italic) &&
                <button className="ql-italic" aria-label={formatMessage({ id: 'richTextFieldItalic' })}></button>
            }
            {(options.all || options.underline) &&
                <button className="ql-underline" aria-label={formatMessage({ id: 'richTextFieldUnderline' })}></button>
            }
            {(options.all || options.strikethrough) &&
                <button className="ql-strike" aria-label={formatMessage({ id: 'richTextFieldStrike' })}></button>
            }
            {(options.all || options.blockquote) &&
                <button className="ql-blockquote" aria-label={formatMessage({ id: 'richTextFieldBlock' })}></button>
            }
            {(options.all || options.blockquote) &&
                <button className="ql-code-block" aria-label={formatMessage({ id: 'richTextFieldCode' })}></button>
            }
            {(options.all || options.orderedList) &&
                <button className="ql-list" value="ordered" aria-label={formatMessage({ id: 'richTextFieldOrdered' })}></button>
            }
            {(options.all || options.bulletList) &&
                <button className="ql-list" value="bullet" aria-label={formatMessage({ id: 'richTextFieldBullet' })}></button>
            }
            {(options.all || options.direction) &&
                <button className="ql-direction" value="rtl" aria-label={formatMessage({ id: 'richTextFieldDirection' })}></button>
            }
            {(options.all || options.header) &&
                <select className="ql-header" defaultValue="" aria-label={formatMessage({ id: 'richTextFieldHeader' })}>
                    <option value="1">{formatMessage({ id: 'quillToolbarHeading1' })}</option>
                    <option value="2">{formatMessage({ id: 'quillToolbarHeading2' })}</option>
                    <option value="3">{formatMessage({ id: 'quillToolbarHeading3' })}</option>
                    <option value="4">{formatMessage({ id: 'quillToolbarHeading4' })}</option>
                    <option value="5">{formatMessage({ id: 'quillToolbarHeading5' })}</option>
                    <option value="6">{formatMessage({ id: 'quillToolbarHeading6' })}</option>
                    <option value="">{formatMessage({ id: 'quillToolbarHeadingNormal' })}</option>
                </select>
            }
            {(options.all || options.color) &&
                <select className="ql-color" aria-label={formatMessage({ id: 'richTextFieldColor' })}></select>
            }
            {(options.all || options.backgroundColor) &&
                <select className="ql-background" aria-label={formatMessage({ id: 'richTextFieldBackgroundColor' })}></select>
            }
            {(options.all || options.font) && !!fontNames?.length &&
                <select className="ql-font" defaultValue={''} aria-label={formatMessage({ id: 'richTextFieldFont' })}>
                    {fontNames.map((it, idx) => (
                        <option key={idx} value={it}></option>
                    ))}
                </select>
            }
            {(options.all || options.link) &&
                <button className="ql-link" aria-label={formatMessage({ id: 'richTextFieldHyperlink' })}></button>
            }
            {(options.all || options.align) &&
                <button className="ql-align" aria-label={formatMessage({ id: 'richTextFieldAlign' })}></button>
            }
            {placeholders?.length > 0 &&
                <select className="ql-placeholder" aria-label={formatMessage({ id: 'richTextFieldPlaceholder' })}>
                    {placeholders.map((it, idx) => (
                        <option key={idx} value={it.id}>{it.label}</option>
                    ))}
                </select>
            }
            {options.formLink &&
                <button id="formlink" className="ql-formlink" aria-label={formatMessage({ id: 'richTextFieldFormLink' })}>{formatMessage({id: 'richTextFieldFormLink'})}</button>
            }
            {(options.all || options.clean) &&
                <button className="ql-clean" aria-label={formatMessage({ id: 'richTextFieldClean' })}></button>
            }
        </div>
    );
};
const CustomToolbarWrapper = CustomToolbar;

const RichTextField = props => {
    const reactQuillRef = React.useRef();
    const { formatMessage } = useIntl();
    const snackbar = useSnackbar();
    const {
        helperText: helperTextProp = '',
        input,
        value,
        maxCharacters,
        name: nameProp,
        onBlur: onBlurProp,
        placeholder,
        required = false,
        requiredErrorTextId = 'validateRequired',
        idForMaxChars,
        toolbarOptions,
        placeholders: placeholdersProp = false,
        errors,
        ...rest
    } = props;
    const toolbarId = useMemo(() => `quill-toolbar-${uuid()}`, []);
    useEffect(() => {
        const formLinkButton = document.querySelector('#formlink');
        const formLinkFn = () => {
            const editor = reactQuillRef.current.getEditor();
            const unprivilegedEditor = reactQuillRef.current.makeUnprivilegedEditor(editor);
            const range = unprivilegedEditor?.getSelection();
            if ( range != null && range?.index > 0) { 
                const text = unprivilegedEditor?.getText(range.index, range.length);
                editor.deleteText(range.index, range.length);
                editor.pasteHTML(range.index, `<span class="ql-placeholder-content" data-id="formlink" data-label="${text}" spellcheck="false">`);
            }
        };
        formLinkButton?.addEventListener('click', formLinkFn);

        const toolbar = document.getElementById(toolbarId);
        const mousedownFn = e => {
            e.preventDefault();
        };
        toolbar?.addEventListener('mousedown', mousedownFn);

        let editor;
        const pasteFn = function (e) {
            const editor = reactQuillRef.current.getEditor();
            const unprivilegedEditor = reactQuillRef.current.makeUnprivilegedEditor(editor);
            if (unprivilegedEditor?.getLength() > maxCharacters) {
                e.preventDefault();
                snackbar.show(formatMessage({ id: 'appDesignConfigurationSupplementalItemsDialogDescriptionCopyWarning' }));
            }
            let paste = (e.clipboardData || window.clipboardData).getData('text');
            if (paste.length > maxCharacters || paste.length + unprivilegedEditor?.getLength() > maxCharacters) {
                e.preventDefault();
                snackbar.show(formatMessage({ id: 'appDesignConfigurationSupplementalItemsDialogDescriptionCopyWarning' }));
            }
        };
        if (maxCharacters) {
            editor = document.getElementById(idForMaxChars);
            editor?.addEventListener('paste', pasteFn);
        }

        return () => {
            editor?.removeEventListener('paste', pasteFn);
            toolbar?.removeEventListener('mousedown', mousedownFn);
            formLinkButton?.removeEventListener('click', formLinkFn);
        };
    }, []);
    useRendersCount(`RichTextField( ${nameProp} )`);
    const { classes, cx } = useStyles();
    const [touched, setTouched] = useState(false);

    const onBlur = useCallback(e => {
        setTouched(true);
        onBlurProp && onBlurProp(e);
    }, [onBlurProp]);

    const error = useMemo(() => {
        if (required && (!value || value === '<p><br></p>')) {
            return formatMessage({ id: requiredErrorTextId });
        }
    }, [required, formatMessage, requiredErrorTextId, value]);

    let hasError = false;
    let helperText = helperTextProp;
    if ((error && touched) || errors) {
        hasError = true;
        helperText = error;
    }

    const checkCharacterCount = useCallback(event => {
        if (event.target.hasAttribute('data-formula')) {
            return;
        }
        const editor = reactQuillRef.current.getEditor();
        const unprivilegedEditor = reactQuillRef.current.makeUnprivilegedEditor(editor);
        if (unprivilegedEditor?.getText().length > maxCharacters && event.key !== 'Backspace') {
            event.preventDefault();
        }
    }, [maxCharacters, reactQuillRef.current]);
    const additionalClasses = useMemo(() => {
        return cx(classes.root);
    }, [classes]);
    const modules = useMemo(() => {
        const config = {
            toolbar: `#${toolbarId}`,
            placeholder: {
                delimiters: ['{', '}'],
                placeholders: [],
            },
            maxlength: {
                value : maxCharacters
            },
            history: {
                delay: 100,
                userOnly: true
            }
        };
        if (placeholdersProp === true) {
            config.placeholder.placeholders = [...getDefaultPlaceholders().map(it => ({
                id: it.id,
                label: formatMessage({id: it.label})
            }))];
        } else if (placeholdersProp !== false) {
            config.placeholder.placeholders = [...placeholdersProp];
        }
        return config;
    }, [toolbarId, formatMessage, placeholdersProp]);
    const helperTextClasses = useMemo(() => {
        return cx({
            [classes.errorText]: (hasError && touched) || errors,
        });
    }, [classes, hasError, touched]);
    return (
        <>
            <GlobalStyles styles={{ '.ql-picker-options': { zIndex: `${zIndexDropdown} !important` } }} />
            <div className={hasError ? classes.error : undefined}>
                <CustomToolbarWrapper id={toolbarId} options={toolbarOptions} placeholders={placeholdersProp} />
                <ReactQuill
                    className={additionalClasses}
                    modules={modules}
                    placeholder={placeholder || null}
                    value={value}
                    {...rest}
                    {...input}
                    onKeyDown={maxCharacters && checkCharacterCount}
                    onBlur={onBlur}
                    ref={reactQuillRef}
                    theme="snow"
                    bounds={'#Quill'}
                />
            </div>
            <div className={classes.helperText}>
                <Typography variant="body3" className={helperTextClasses}>{helperText}</Typography>
            </div>
        </>
    );
};
export default RichTextField;
